import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { permissionChecks } from '../../../utils/permission-check';
import { InputHandler } from '../../../viewer/input-handler';
import { SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { ProjectGeodataManager } from '../../../viewer/project-geodata-manager';
import { SceneManager } from '../../../viewer/scene-manager';
import { useSubscribe } from '../../hooks/use-subscribe';
import { useOptionalUserState } from '../../state/user-state/user-state';
import { ThemeManager } from '../../themes/theme-manager';
import { Chip, ChipStyled } from '../chip/chip';
import {
  IntegrationAsbuiltStatus,
  IntegrationsContext,
} from '../integrations/state/integrations-state';
import { AsbuiltPanel } from '../skyview/panels/asbuilt/asbuilt-panel';
import { ShareView } from './components/share-view/share-view';

const SkyviewChipsContainer = () => {
  const { t } = useTranslation();
  const userState = useOptionalUserState();
  const [showShareSkyViewPanel, setShowShareSkyViewPanel] = React.useState(false);
  const [showAsbuiltPanel, setShowAsbuiltPanel] = React.useState(false);
  const [activeSceneType, setActiveSceneType] = React.useState(
    SceneManager.instance.getActiveSceneType(),
  );

  const userHasAccessToOnDemand = userState?.isOrganizationAdmin || userState?.isCompanyAdmin;

  useSubscribe(SubscriptionTopic.SetTool, (tool) => {
    setShowAsbuiltPanel(tool?.name === 'asbuilt-geometry');
  });

  useSubscribe(SubscriptionTopic.OnSceneChangeCompleted, (message) => {
    setActiveSceneType(message.scene);
  });

  const integrationsContext = React.useContext(IntegrationsContext);
  const hasAsbuiltIntegration =
    integrationsContext?.integrationAsbuiltSupportState?.() === IntegrationAsbuiltStatus.Supported;

  const isPanoramaViewActive = activeSceneType === 'panoramaViewer';

  const showShareSkyViewChip =
    !isPanoramaViewActive && ProjectGeodataManager.instance().hasTimelineGeodata();
  const showOnDemandChip = !isPanoramaViewActive && ThemeManager.instance.isSwedishDomain();
  const showAsbuiltChip =
    !isPanoramaViewActive &&
    (hasAsbuiltIntegration || permissionChecks.hasAccessToFeature('feature:viewer:chips:as_built'));

  return (
    <Component>
      {showShareSkyViewChip && (
        <Chip
          leftArea={{
            icon: {
              icon: 'share-nodes',
            },
          }}
          padding="medium"
          text={t('shareView.chipText', { ns: 'skyview' })}
          onClick={() => {
            setShowShareSkyViewPanel(!showShareSkyViewPanel);
          }}
        />
      )}

      {showOnDemandChip && (
        <Chip
          disabled={!userHasAccessToOnDemand}
          leftArea={{
            icon: {
              icon: 'rocket',
            },
          }}
          padding="medium"
          text={t('onDemand.title', { ns: 'components' })}
          title={
            !userHasAccessToOnDemand ? t('onDemand.noAccess', { ns: 'components' }) : undefined
          }
          onClick={() => {
            InputHandler.instance.setTool(showAsbuiltPanel ? 'select' : 'on-demand');
          }}
        />
      )}
      {showAsbuiltChip && (
        <Chip
          leftArea={{
            icon: {
              icon: 'chart-network',
            },
          }}
          padding="medium"
          text="As-built"
          onClick={() => {
            InputHandler.instance.setTool(showAsbuiltPanel ? 'select' : 'asbuilt-geometry');
          }}
        />
      )}
      {showShareSkyViewPanel && <ShareView />}
      {showAsbuiltPanel && <AsbuiltPanel />}
    </Component>
  );
};

const Component = styled.div`
  display: flex;
  justify-content: right;
  align-items: center;
  margin: 1em;
  gap: 1.5em;

  ${ChipStyled} {
    font-size: 14px;
  }
`;

export { SkyviewChipsContainer };
